import { useCallback, useEffect, useRef, useState } from 'react';

import type { ExecutionsHistorySectionResult } from '@src/models/interfaces/executionsHistoryDashboard.interface';
import ExecutionsHistoryDashboardService from '@src/services/ExecutionsHistoryDashboardService';

export interface HistorySectionState {
  loading: boolean;
  result?: ExecutionsHistorySectionResult;
}

export type HistorySectionsState<TSection extends string> = Record<TSection, HistorySectionState>;

type CachedResult = { fetchedAt: number; result: ExecutionsHistorySectionResult };
const CACHE_MS = 30_000;

export interface HistorySectionsConfig<TQuery, TSection extends string> {
  /** Report sections to load together; pass a stable module-level array. */
  sections: readonly TSection[];
  /** Cache/dedupe key per section (e.g. the overview section may add the series interval). */
  queryKey: (section: TSection, query: TQuery) => string;
  /** Calls this report's one section endpoint (Schedules, Routes, ...). */
  loadSection: (
    service: ExecutionsHistoryDashboardService,
    section: TSection,
    query: TQuery,
  ) => Promise<ExecutionsHistorySectionResult>;
}

/**
 * Loads report sections independently, with per-section cache/retry. Shared by every
 * executions-history report; entity-specific wrappers only supply the section list and
 * the service call (see `useScheduleHistorySections` / `useRouteHistorySections`).
 */
export const useHistorySections = <TQuery, TSection extends string>(
  query: TQuery | undefined,
  { sections, queryKey, loadSection: callSection }: HistorySectionsConfig<TQuery, TSection>,
) => {
  const serviceRef = useRef<ExecutionsHistoryDashboardService | null>(null);
  if (!serviceRef.current) serviceRef.current = new ExecutionsHistoryDashboardService();
  const queryRef = useRef(query);
  queryRef.current = query;
  const generationsRef = useRef<Partial<Record<TSection, number>>>({});
  const requestedKeysRef = useRef<Partial<Record<TSection, string>>>({});
  const cacheRef = useRef(new Map<string, CachedResult>());
  const [sectionsState, setSectionsState] = useState<HistorySectionsState<TSection>>(
    () => Object.fromEntries(sections.map(section => [section, { loading: true }])) as HistorySectionsState<TSection>,
  );

  const loadSection = useCallback(
    async (section: TSection, force = false) => {
      const currentQuery = queryRef.current;
      if (!currentQuery) return;
      const key = queryKey(section, currentQuery);
      if (!force && requestedKeysRef.current[section] === key) return;
      requestedKeysRef.current[section] = key;
      const generation = (generationsRef.current[section] ?? 0) + 1;
      generationsRef.current[section] = generation;
      const cacheKey = `${section}|${key}`;
      const cached = cacheRef.current.get(cacheKey);
      if (!force && cached && Date.now() - cached.fetchedAt < CACHE_MS) {
        setSectionsState(current => ({
          ...current,
          [section]: { loading: false, result: cached.result },
        }));
        return;
      }
      setSectionsState(current => ({
        ...current,
        [section]: { ...current[section], loading: true },
      }));
      const result = await callSection(serviceRef.current!, section, currentQuery);
      if (result.status === 'ok') cacheRef.current.set(cacheKey, { fetchedAt: Date.now(), result });
      if (generationsRef.current[section] !== generation) return;
      setSectionsState(current => ({ ...current, [section]: { loading: false, result } }));
    },
    [callSection, queryKey],
  );

  const queryJson = JSON.stringify(query);
  useEffect(() => {
    if (!query) return;
    sections.forEach(section => {
      void loadSection(section);
    });
  }, [loadSection, queryJson, query, sections]);

  const retrySection = useCallback(
    (section: TSection) => {
      void loadSection(section, true);
    },
    [loadSection],
  );
  const refresh = useCallback(() => {
    sections.forEach(section => {
      void loadSection(section, true);
    });
  }, [loadSection, sections]);
  const loading = sections.some(section => sectionsState[section].loading);

  return { sections: sectionsState, loading, refresh, retrySection };
};
